import { useEffect, useRef } from "react";
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, highlightActiveLine } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { HighlightStyle, syntaxHighlighting, indentUnit } from "@codemirror/language";
import { python } from "@codemirror/lang-python";
import { tags } from "@lezer/highlight";

// Подсветка по A0: ключевые слова — акцент, строки — жёлтым, числа — синим,
// комментарии приглушённо курсивом, имена функций — основным жирным.
const highlight = HighlightStyle.define([
  { tag: [tags.keyword, tags.controlKeyword, tags.definitionKeyword, tags.bool, tags.null, tags.self], color: "var(--acc)" },
  { tag: [tags.string, tags.special(tags.string)], color: "var(--warn)" },
  { tag: [tags.number], color: "var(--info)" },
  { tag: [tags.comment], color: "var(--mut)", fontStyle: "italic" },
  { tag: [tags.function(tags.variableName), tags.function(tags.definition(tags.variableName))], color: "var(--ink)", fontWeight: "600" },
]);

const theme = EditorView.theme({
  "&": { background: "transparent", color: "var(--ink)", fontSize: "12.5px" },
  ".cm-content": { fontFamily: "var(--mono)", lineHeight: "1.65", padding: "12px 0", caretColor: "var(--acc)" },
  ".cm-gutters": { background: "transparent", border: "none", color: "var(--mut)" },
  ".cm-lineNumbers .cm-gutterElement": { padding: "0 12px 0 10px", opacity: "0.7" },
  ".cm-activeLine": { background: "color-mix(in oklch, var(--card2) 60%, transparent)" },
  "&.cm-focused": { outline: "none" },
  ".cm-selectionBackground, &.cm-focused .cm-selectionBackground, ::selection": { background: "var(--accsoft)" },
  ".cm-cursor": { borderLeftColor: "var(--acc)" },
});

/** Ячейка кода на CodeMirror 6: Python, нумерация строк, Shift+Enter — запуск. */
export function CodeEditor({ value, onChange, onRun, ariaLabel }: { value: string; onChange: (v: string) => void; onRun: () => void; ariaLabel: string }) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  const cb = useRef({ onChange, onRun });
  cb.current = { onChange, onRun };

  useEffect(() => {
    if (!host.current) return;
    const v = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: value,
        extensions: [
          lineNumbers(),
          highlightActiveLine(),
          history(),
          indentUnit.of("    "),
          keymap.of([{ key: "Shift-Enter", run: () => (cb.current.onRun(), true) }, indentWithTab, ...defaultKeymap, ...historyKeymap]),
          python(),
          syntaxHighlighting(highlight),
          theme,
          EditorView.contentAttributes.of({ "aria-label": ariaLabel }),
          EditorView.updateListener.of((u) => {
            if (u.docChanged) cb.current.onChange(u.state.doc.toString());
          }),
        ],
      }),
    });
    view.current = v;
    return () => v.destroy();
    // Документ задаётся при монтировании; смена задачи пересоздаёт редактор через key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={host} className="min-h-[150px]" data-testid="code-editor" />;
}
